import { Button } from "primereact/button";
import { Card } from "primereact/card";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { RoutePaths, StatusesColors } from "src/resources/enum";
import DoughnutChart from "src/components/Charts/DoughnutChart";
import { ProgressSpinner } from "primereact/progressspinner";
import { formatNumber, isCivilDefenseUser } from "src/utils/helpers";

const ContractsCard = () => {
  const { t } = useTranslation(["common", "dashboard"]);
  const navigate = useNavigate();

  const [chartsData, setChartsData] = useState<any>();
  const { language } = useSelector((state: any) => state.lookup);

  const {
    contractInformationData,
    contractInformationTotalCount,
    isContractInformationDataLoaded,
  } = useSelector((state: any) => state.dashboard);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "83%",
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: false, // Ensure datalabels are hidden
      },
      // centerTextPlugin: {}, // Register the plugin locally
    },
    rotation: -90, // Start point
    circumference: 180,
  };

  useEffect(() => {
    const keysArray = Object.keys(contractInformationData);
    const valuesArray = Object.values(contractInformationData);

    if (valuesArray.some((value) => (value as number) > 0)) {
      const newChartsData = {
        data: valuesArray,
        color: [
          StatusesColors.Active,
          StatusesColors.Expiringin90Days,
          StatusesColors.Expiringin30Days,
        ],
        label: keysArray,
      } as any;

      setChartsData(newChartsData);
    } else {
      const newChartsData = {
        data: [1],
        color: [StatusesColors.Planck],
        label: keysArray,
      } as any;
      setChartsData(newChartsData);
    }
  }, [
    contractInformationData,
    contractInformationTotalCount,
    isContractInformationDataLoaded,
    language,
  ]);
  return (
    <Card className="card card-3-2">
      <div className="card-content">
        <div className="d-flex  justify-content-between align-items-center pt-2">
          <h6 className="card-title">{t("activeContracts")}</h6>
          {!isCivilDefenseUser() && (
            <Button
              label={t("seeAll")}
              className="edit-btn light-primary-btn m-0"
              onClick={() => navigate(RoutePaths.AssignedBuildings)}
            />
          )}
        </div>
        <div className="d-flex align-items-center justify-content-center flex-column  ">
          {isContractInformationDataLoaded &&
          chartsData?.data?.length &&
          chartsData.data[0] !== undefined ? (
            <div
              className="contacts-chart-container"
              title={contractInformationTotalCount}
            >
              <DoughnutChart
                chartData={{
                  labels: chartsData.label,
                  datasets: [
                    {
                      data: chartsData.data,
                      backgroundColor: chartsData.color,
                    },
                  ],
                }}
                options={chartOptions}
                centerText={[
                  {
                    text: formatNumber(contractInformationTotalCount),
                    fontSize: 40,
                    fontWeight: "normal",
                    xOffset: 0,
                    yOffset: +20,
                  },
                  {
                    text: "Contracts",
                    fontSize: 14,
                    fontWeight: "normal",
                    xOffset: 0,
                    yOffset: +50,
                  },
                ]}
              />
              <div className="stats-row contract-stats">
                <p className="stats-row__item">
                  <div className="d-flex gap-2 align-items-center">
                    <span
                      className="dot"
                      style={{ backgroundColor: StatusesColors.Active }}
                    ></span>
                    <span
                      className="font-weight-500"
                      title={contractInformationData.activeContracts}
                    >
                      {contractInformationData
                        ? formatNumber(contractInformationData.activeContracts)
                        : ""}
                    </span>
                  </div>
                  <span className="contract-status">{t("contracts")}</span>
                </p>
                <p className="stats-row__item">
                  <div className="d-flex gap-2 align-items-center ">
                    <span
                      className="dot"
                      style={{
                        backgroundColor: StatusesColors.Expiringin90Days,
                      }}
                    ></span>
                    <span
                      className="font-weight-500"
                      title={contractInformationData.expiringIn90Days}
                    >
                      {contractInformationData
                        ? formatNumber(contractInformationData.expiringIn90Days)
                        : ""}
                    </span>
                  </div>
                  <span className="contract-status">
                    {t("expiringIn90Days")}
                  </span>
                </p>
                <p className="stats-row__item">
                  <div className="d-flex gap-2 align-items-center">
                    <span
                      className="dot"
                      style={{
                        backgroundColor: StatusesColors.Expiringin30Days,
                      }}
                    ></span>
                    <span
                      className="font-weight-500"
                      title={contractInformationData?.expiringIn30Days}
                    >
                      {contractInformationData
                        ? formatNumber(contractInformationData.expiringIn30Days)
                        : ""}
                    </span>
                  </div>
                  <span className="contract-status">
                    {t("expiringIn30Days")}
                  </span>
                </p>
              </div>
              <p className="note mt-2">
                {t("notAffectedByFilterDate", {
                  field: t("theContracts"),
                })}
              </p>
            </div>
          ) : (
            <ProgressSpinner></ProgressSpinner>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ContractsCard;
